import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { GoogleLogin } from '@react-oauth/google';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from '../../utility/axios';
import '../../styles/auth/BecomeGuest.css';

const BecomeGuest = () => {
    const [email, setEmail] = useState('');
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [checkingAccess, setCheckingAccess] = useState(true);
    const [emailLocked, setEmailLocked] = useState(false);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const googleLocale = i18n.language?.startsWith('fr') ? 'fr' : 'en';

    useEffect(() => {
        let cancelled = false;
        fetch('/api/contacts-mailbox/status/', { credentials: 'include' })
            .then((res) => res.json())
            .then((data) => {
                if (!cancelled) {
                    if (!data.has_mailbox_access) {
                        navigate('/mailbox', { replace: true });
                    } else if (data.email_is_free_account) {
                        navigate('/mailbox', { replace: true });
                    } else if (data.receiver_email && String(data.receiver_email).trim()) {
                        setEmail(String(data.receiver_email).trim());
                        setEmailLocked(true);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) navigate('/mailbox', { replace: true });
            })
            .finally(() => {
                if (!cancelled) setCheckingAccess(false);
            });
        return () => { cancelled = true; };
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!email?.trim() || !firstname?.trim() || !lastname?.trim() || !password?.trim() || !confirmPassword?.trim()) {
            setError(t('contactsMailbox.fillAllFields'));
            return;
        }
        if (password.length < 6) {
            setError(t('auth.register.passwordMinLength'));
            return;
        }
        if (password !== confirmPassword) {
            setError(t('auth.register.passwordMismatch'));
            return;
        }
        setIsLoading(true);
        try {
            await axios.post('/api/contacts-mailbox/become-guest/', {
                email: email.trim(),
                firstname: firstname.trim(),
                lastname: lastname.trim(),
                password: password.trim(),
                password_confirm: confirmPassword.trim(),
            });
            navigate('/mailbox', { replace: true, state: { becomeGuestSuccess: true } });
        } catch (err) {
            setError(err.response?.data?.error || t('contactsMailbox.becomeGuestFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleSuccess = async (credentialResponse) => {
        setError('');
        setIsLoading(true);
        try {
            await axios.post('/api/contacts-mailbox/become-guest/google/', {
                token: credentialResponse.credential,
            });
            navigate('/mailbox', { replace: true, state: { becomeGuestSuccess: true } });
        } catch (err) {
            setError(err.response?.data?.error || t('contactsMailbox.becomeGuestFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    if (checkingAccess) {
        return (
            <div className="loginFormContainer becomeGuestContainer">
                <div className="becomeGuestLoading">{t('auth.login.loading')}</div>
            </div>
        );
    }

    return (
        <div className="loginFormContainer becomeGuestContainer">
            <h2 className="loginFormTitle">{t('contactsMailbox.becomeGuestTitle')}</h2>
            {error && <div className="errorMessage">{error}</div>}
            <div className="social-login">
                <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID} locale={googleLocale}>
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError(t('auth.register.googleSignInFailed'))}
                        theme="outline"
                        size="large"
                        text="signup_with"
                    />
                </GoogleOAuthProvider>
            </div>
            <div className="loginSeperator"></div>
            <form onSubmit={handleSubmit} className="loginForm">
                <div className="loginUsernameForm">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => { if (!emailLocked) setEmail(e.target.value); }}
                        readOnly={emailLocked}
                        required
                        className={`loginFormInput${emailLocked ? ' loginFormInput-readOnly' : ''}`}
                        placeholder={t('auth.register.emailPlaceholder')}
                        autoComplete="email"
                        aria-readonly={emailLocked || undefined}
                    />
                </div>
                <div className="loginUsernameForm">
                    <input
                        type="text"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        required
                        className="loginFormInput"
                        placeholder={t('auth.register.firstNamePlaceholder')}
                    />
                </div>
                <div className="loginUsernameForm">
                    <input
                        type="text"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        required
                        className="loginFormInput"
                        placeholder={t('auth.register.lastNamePlaceholder')}
                    />
                </div>
                <div className="loginPasswordForm">
                    <div className="passwordInputContainer">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="loginFormInput"
                            placeholder={t('auth.register.passwordPlaceholder')}
                        />
                        <FontAwesomeIcon
                            icon={showPassword ? faEyeSlash : faEye}
                            onClick={() => setShowPassword(!showPassword)}
                            className="passwordToggleIcon"
                        />
                    </div>
                </div>
                <div className="loginPasswordForm">
                    <div className="passwordInputContainer">
                        <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            className="loginFormInput"
                            placeholder={t('auth.register.confirmPasswordPlaceholder')}
                        />
                        <FontAwesomeIcon
                            icon={showConfirmPassword ? faEyeSlash : faEye}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="passwordToggleIcon"
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    className="loginFormButton auth-btn-guest"
                    disabled={isLoading}
                >
                    {isLoading ? t('auth.register.registering') : t('contactsMailbox.becomeGuestSubmit')}
                </button>
            </form>
            <div className="loginSeperator"></div>
            <div className="registerDescription">
                <p>
                    {t('contactsMailbox.becomeGuestBackToMailbox')}{' '}
                    <Link to="/mailbox" className="register-link">{t('layout.navigation.mailbox')}</Link>
                </p>
            </div>
        </div>
    );
};

export default BecomeGuest;
